import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";

const CONSENT_STORAGE_KEY = "analytics-consent";
const CONSENT_DOMAIN_KEY = "analytics-consent-domain";

interface CookieConsentPanelProps {
  onConsentChange?: (granted: boolean) => void;
}

export const CookieConsentPanel: React.FC<CookieConsentPanelProps> = ({
  onConsentChange,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Check if user has already made a decision for this domain
    const currentDomain = window.location.hostname;
    const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
    const storedDomain = localStorage.getItem(CONSENT_DOMAIN_KEY);

    // Show panel if no consent exists OR if the domain has changed
    if (!storedConsent || storedDomain !== currentDomain) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    const currentDomain = window.location.hostname;
    localStorage.setItem(CONSENT_STORAGE_KEY, "granted");
    localStorage.setItem(CONSENT_DOMAIN_KEY, currentDomain);

    if (onConsentChange) {
      onConsentChange(true);
    }

    setIsClosing(true);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleDecline = () => {
    const currentDomain = window.location.hostname;
    localStorage.setItem(CONSENT_STORAGE_KEY, "denied");
    localStorage.setItem(CONSENT_DOMAIN_KEY, currentDomain);

    if (onConsentChange) {
      onConsentChange(false);
    }

    setIsClosing(true);
    setTimeout(() => setIsVisible(false), 300);
  };

  const handleDismiss = () => {
    // Dismiss without saving - will show again on next visit
    setIsClosing(true);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: isClosing ? 0 : 1, y: isClosing ? 50 : 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 sm:p-6"
        role="dialog"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
      >
        <div className="mx-auto max-w-2xl">
          <div className="relative rounded-xl border border-gray-200 bg-white/95 p-5 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-gray-900/95">
            {/* Close button */}
            <button
              onClick={handleDismiss}
              className="absolute right-3 top-3 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
              aria-label="Dismiss cookie notice"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5">
              {/* Icon */}
              <div className="flex-shrink-0">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                  <Cookie className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 pr-6 sm:pr-0">
                <h3
                  id="cookie-consent-title"
                  className="text-base font-medium text-gray-700 dark:text-gray-200"
                >
                  We value your privacy
                </h3>
                <p
                  id="cookie-consent-description"
                  className="mt-1 text-sm leading-relaxed text-gray-500 dark:text-gray-400"
                >
                  We use analytics cookies to understand how you interact with
                  our site and improve your experience. Your data helps us
                  create better content and features for you.
                </p>
              </div>

              {/* Actions */}
              <div className="flex flex-row gap-2 sm:flex-shrink-0">
                <button
                  onClick={handleDecline}
                  className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Decline
                </button>
                <button
                  onClick={handleAccept}
                  className="rounded-lg bg-gray-800 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:bg-gray-200 dark:text-gray-900 dark:hover:bg-gray-300"
                >
                  Accept
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
